/**
 * PSP/CRE Tool v2.2 - MTF (Medical Treatment Facility) Capabilities
 * 
 * Defines medical treatment facility structure and capabilities by force echelon.
 * Based on NATO medical planning doctrine and national standards.
 * 
 * @fileoverview MTF capabilities, bed capacity, surgical teams, and logistics
 * @module constants/mtfCapabilities
 * @author Dr. Tatar Radu MD
 * @date January 8, 2025
 * @version 2.3 - DOCTRINAL-005: align setup_time_hours with AJMedP-1 (March 15, 2026)
 *   R2B: 4h -> 12h | R2E: 8h -> 24h | R3: 72h -> 48h
 *   Source corrected to AJMedP-1 Section 3.4 throughout.
 *   teardown_time_hours updated proportionally (AJMedP-1 does not specify separately;
 *   50% of setup time used as NATO planning estimate).
 *   No functional impact — deployment block is display-only; calculator uses
 *   MTF_SETUP_TIMES_HOURS from casualtyCalculatorConstants.js (already correct).
 *
 * @version 2.2 - Added ICU beds to Role 2 Enhanced and echelon capabilities
 * 
 * VERSION 2.2 CHANGES:
 * - Added icu_beds to ROLE_2_ENHANCED_DEFINITION holding_capacity (10 beds)
 * - Added icu_beds to MTF_CAPABILITIES_BRIGADE.role2 (0 - R2B has no ICU)
 * - Added icu_beds to MTF_CAPABILITIES_DIVISION.role2 (0 - R2B has no ICU)
 * - Added icu_beds to MTF_CAPABILITIES_CORPS.role2 (0 - R2B has no ICU)
 * - ICU capability is at Role 2 Enhanced and Role 3 only
 * 
 * MTF Roles (NATO Standard):
 * - Role 1: Battalion Aid Station (BAS) - Basic emergency care, stabilization
 * - Role 2 Basic: Forward Resuscitation (Brigade level) - Damage control resuscitation
 * - Role 2 Enhanced: Forward Surgical Team (Division level) - Damage control surgery
 * - Role 3: Combat Support Hospital (Corps level) - Definitive surgical care
 * - Role 4: Strategic Hospital (National territory) - Full medical care, rehabilitation
 * 
 * CRITICAL HIERARCHY:
 * - BATTALION: Role 1 only (6 beds, no surgery)
 * - BRIGADE: Role 1 + Role 2 Basic (30 beds, 2 surgical teams)
 * - DIVISION: Role 1 (×3) + Role 2 Enhanced (×3, 6 total surgical teams)
 * - CORPS: Role 1 (×6) + Role 2 (×6) + Role 3 (Combat Support Hospital)
 * 
 * VERSION 2.1 CHANGES:
 * - icons.js integration (MEDICAL_ICONS, STATUS_ICONS)
 * - Added getMTFRoleOptions() for UI dropdowns
 * - Added getEchelonOptions() for UI
 * - Added validateMTFConfig() function
 * - Enhanced educational metadata
 * - Added Ukraine 2022-2024 MTF lessons
 */

// ============================================================================
// ICONS IMPORT
// ============================================================================

import {
  STATUS_ICONS,
  MEDICAL_ICONS,
  TACTICAL_ICONS
} from '../utils/icons';

// ============================================================================
// ICON DEFINITIONS (Fallback if icons.js not available)
// ============================================================================

const ICONS = {
  // Status icons
  GREEN: STATUS_ICONS?.GREEN || '🟢',
  YELLOW: STATUS_ICONS?.YELLOW || '🟡',
  RED: STATUS_ICONS?.RED || '🔴',
  ORANGE: STATUS_ICONS?.ORANGE || '🟠',
  
  // Medical icons
  MEDICAL: MEDICAL_ICONS?.CROSS || '➕',
  HOSPITAL: MEDICAL_ICONS?.HOSPITAL || '🏥',
  BED: MEDICAL_ICONS?.BED || '🛏️',
  SURGERY: MEDICAL_ICONS?.SURGERY || '🔪',
  BLOOD: MEDICAL_ICONS?.BLOOD || '🩸',
  
  // Tactical icons
  WARNING: TACTICAL_ICONS?.WARNING || '⚠️'
};

// ============================================================================
// MTF ROLE DEFINITIONS
// ============================================================================

/**
 * Role 1: Battalion Aid Station (BAS)
 */
export const ROLE_1_DEFINITION = {
  name: 'Role 1 - Battalion Aid Station',
  abbreviation: 'BAS',
  nato_role: 'Role 1',
  echelon: 'Battalion',
  icon: ICONS.MEDICAL,
  color: '#4CAF50',
  typical_distance_from_feba_km: { min: 5, max: 15, typical: 8 },
  
  mission: 'Triage, basic life support, stabilization for evacuation',
  
  capabilities: {
    triage: true,
    basic_resuscitation: true,
    advanced_life_support: true,
    tccc_interventions: true,
    surgery: false,
    damage_control_surgery: false,
    blood_transfusion: true,
    xray: false,
    ultrasound: false,
    laboratory: false,
    dental: 'EMERGENCY_ONLY'
  },
  
  holding_capacity: {
    beds: 6,
    bed_type: 'STRETCHER',
    hold_time_hours: 4,
    surge_capacity: 12
  },
  
  treatment_capabilities: {
    hemorrhage_control: true,
    tourniquet: true,
    hemostatic_dressings: true,
    airway_management: true,
    chest_decompression: true,
    iv_access: true,
    blood_products: 'O_NEG_ONLY',
    pain_management: true,
    hypothermia_prevention: true,
    splinting: true,
    wound_care: 'BASIC'
  },
  
  patient_throughput: {
    normal_per_hour: 4,
    surge_per_hour: 8,
    max_per_day: 60
  },
  
  deployment: {
    setup_time_hours: 1,
    teardown_time_hours: 0.5,
    mobility: 'HIGH',
    vehicles_required: 2,
    can_displace_with_patients: true
  },
  
  typical_patient_flow: [
    'Receive casualties from point of injury',
    'Perform triage (T1/T2/T3/T4/Expectant)',
    'Provide emergency treatment',
    'Stabilize for evacuation',
    'Evacuate to Role 2 within 4 hours'
  ],
  
  educational: {
    key_point: 'First echelon medical care - stop bleeding, start breathing',
    teaching_note: 'Focus on TCCC interventions, NOT definitive care',
    common_mistake: 'Holding patients too long instead of evacuating'
  }
};

/**
 * Role 2 Basic: Forward Resuscitation
 */
export const ROLE_2_BASIC_DEFINITION = {
  name: 'Role 2 Basic - Forward Resuscitation',
  abbreviation: 'R2B',
  nato_role: 'Role 2',
  echelon: 'Brigade',
  icon: ICONS.HOSPITAL,
  color: '#2196F3',
  typical_distance_from_feba_km: { min: 15, max: 30, typical: 20 },
  
  mission: 'Damage control resuscitation, limited surgical capability',
  
  capabilities: {
    triage: true,
    advanced_resuscitation: true,
    damage_control_surgery: true,
    blood_bank: true,
    xray: true,
    ultrasound: true,
    laboratory: 'BASIC',
    postoperative_care: 'LIMITED',
    dental: true,
    optometry: 'EMERGENCY'
  },
  
  holding_capacity: {
    beds: 30,
    bed_type: 'MIXED',
    bed_breakdown: {
      emergency: 10,
      surgical: 8,
      recovery: 12
    },
    hold_time_hours: 72,
    surge_capacity: 50
  },
  
  surgical_capability: {
    teams: 2,
    tables: 2,
    procedures_per_day: 8,
    max_procedures_per_day: 12,
    specialties: ['General Surgery', 'Orthopedic Surgery'],
    dcs_capable: true,
    definitive_capable: false
  },
  
  patient_throughput: {
    normal_per_hour: 6,
    surge_per_hour: 12,
    max_per_day: 100,
    surgical_per_day: 8
  },
  
  deployment: {
    setup_time_hours: 12,   // AJMedP-1 Section 3.4 — leap-frog displacement doctrine
    teardown_time_hours: 6, // AJMedP-1 estimate — ~50% of setup time
    mobility: 'MODERATE',
    vehicles_required: 8,
    can_displace_with_patients: false
  },
  
  educational: {
    key_point: 'Damage Control Surgery - operate to stabilize, NOT cure',
    teaching_note: 'DCS goal: hemorrhage control, contamination control, temp closure',
    common_mistake: 'Attempting definitive repair instead of DCS',
    ukraine_lesson: 'Role 2 consistently overwhelmed - need 2x planned capacity'
  }
};

/**
 * Role 2 Enhanced: Forward Surgical Team
 */
export const ROLE_2_ENHANCED_DEFINITION = {
  name: 'Role 2 Enhanced - Forward Surgical Team',
  abbreviation: 'R2E',
  nato_role: 'Role 2 Enhanced',
  echelon: 'Division',
  icon: ICONS.SURGERY,
  color: '#FF9800',
  typical_distance_from_feba_km: { min: 25, max: 60, typical: 40 },
  
  mission: 'Extended damage control surgery, complex resuscitation',
  
  capabilities: {
    triage: true,
    advanced_resuscitation: true,
    damage_control_surgery: true,
    extended_surgery: true,
    blood_bank: true,
    xray: true,
    ct_scan: false,
    ultrasound: true,
    laboratory: 'ENHANCED',
    postoperative_care: 'STANDARD',
    dental: true,
    optometry: true,
    mental_health: 'BASIC'
  },
  
  holding_capacity: {
    beds: 40,
    bed_type: 'MIXED',
    bed_breakdown: {
      emergency: 10,
      surgical: 8,
      icu: 10,        // v2.2: Added ICU beds - limited ICU capability at R2E
      recovery: 12
    },
    icu_beds: 10,     // v2.2: Explicit ICU bed count for ForceBuilder
    hold_time_hours: 72,
    surge_capacity: 80
  },
  
  surgical_capability: {
    teams: 4,
    tables: 4,
    procedures_per_day: 16,
    max_procedures_per_day: 24,
    specialties: [
      'General Surgery',
      'Orthopedic Surgery',
      'Neurosurgery (limited)',
      'Vascular Surgery',
      'Thoracic Surgery (limited)'
    ],
    dcs_capable: true,
    definitive_capable: 'LIMITED'
  },
  
  patient_throughput: {
    normal_per_hour: 8,
    surge_per_hour: 16,
    max_per_day: 150,
    surgical_per_day: 16
  },
  
  deployment: {
    setup_time_hours: 24,    // AJMedP-1 Section 3.4 — division-level facility
    teardown_time_hours: 12, // AJMedP-1 estimate — ~50% of setup time
    mobility: 'LOW',
    vehicles_required: 15,
    can_displace_with_patients: false
  },
  
  educational: {
    key_point: 'Extended DCS capability with limited specialty surgery',
    teaching_note: 'Can handle some neurosurgery/vascular - but still evacuate ASAP',
    ukraine_lesson: 'Forward surgical teams proved critical in contested environment'
  }
};

/**
 * Role 3: Combat Support Hospital (CSH)
 */
export const ROLE_3_DEFINITION = {
  name: 'Role 3 - Combat Support Hospital',
  abbreviation: 'CSH',
  nato_role: 'Role 3',
  echelon: 'Corps',
  icon: ICONS.HOSPITAL,
  color: '#9C27B0',
  typical_distance_from_feba_km: { min: 80, max: 200, typical: 120 },
  
  mission: 'Definitive surgical care, ICU capability, extended holding',
  
  capabilities: {
    triage: true,
    full_resuscitation: true,
    definitive_surgery: true,
    icu: true,
    blood_bank: true,
    xray: true,
    ct_scan: true,
    mri: false,
    ultrasound: true,
    laboratory: 'FULL',
    pharmacy: 'FULL',
    postoperative_care: 'FULL_ICU',
    step_down_care: true,
    dental: true,
    optometry: true,
    mental_health: 'FULL',
    physical_therapy: true
  },
  
  holding_capacity: {
    beds: 150,
    bed_type: 'HOSPITAL',
    bed_breakdown: {
      emergency: 20,
      or_recovery: 20,
      icu: 40,
      step_down: 30,
      ward: 40
    },
    hold_time_hours: 168,
    surge_capacity: 250
  },
  
  surgical_capability: {
    teams: 8,
    tables: 8,
    procedures_per_day: 32,
    max_procedures_per_day: 48,
    specialties: [
      'General Surgery',
      'Orthopedic Surgery',
      'Neurosurgery',
      'Vascular Surgery',
      'Thoracic Surgery',
      'Maxillofacial Surgery',
      'Ophthalmology',
      'ENT Surgery',
      'Urology (limited)',
      'Plastic Surgery (limited)'
    ],
    dcs_capable: true,
    definitive_capable: true,
    complex_procedures: true
  },
  
  patient_throughput: {
    normal_per_hour: 15,
    surge_per_hour: 30,
    max_per_day: 300,
    surgical_per_day: 32,
    icu_admissions_per_day: 20
  },
  
  deployment: {
    setup_time_hours: 48,    // AJMedP-1 Section 3.4 — corps-level hospital
    teardown_time_hours: 24, // AJMedP-1 estimate — ~50% of setup time
    mobility: 'VERY_LOW',
    vehicles_required: 50,
    can_displace_with_patients: false,
    requires_infrastructure: true
  },
  
  educational: {
    key_point: 'First level of DEFINITIVE care - can do complex surgery',
    teaching_note: 'ICU capability is the key differentiator from Role 2',
    ukraine_lesson: 'Role 3 often converted from civilian hospitals'
  }
};

/**
 * Role 4: Strategic Hospital
 */
export const ROLE_4_DEFINITION = {
  name: 'Role 4 - Strategic Hospital',
  abbreviation: 'R4',
  nato_role: 'Role 4',
  echelon: 'National',
  icon: ICONS.HOSPITAL,
  color: '#673AB7',
  typical_distance_from_feba_km: { min: 300, max: null, typical: 500 },
  
  mission: 'Definitive care, rehabilitation, repatriation',
  
  capabilities: {
    all_surgical_specialties: true,
    full_icu: true,
    rehabilitation: true,
    prosthetics: true,
    burn_unit: true,
    spinal_cord_injury: true,
    traumatic_brain_injury: true,
    mental_health: 'COMPREHENSIVE',
    long_term_care: true
  },
  
  holding_capacity: {
    beds: 500,
    bed_type: 'HOSPITAL',
    bed_breakdown: {
      acute_care: 200,
      icu: 50,
      step_down: 100,
      rehabilitation: 100,
      long_term: 50
    },
    hold_time_days: 90,
    surge_capacity: 750
  },
  
  surgical_capability: {
    teams: 20,
    tables: 16,
    procedures_per_day: 50,
    all_specialties: true,
    reconstructive_surgery: true,
    microsurgery: true,
    transplant: 'LIMITED'
  },
  
  rehabilitation: {
    physical_therapy: true,
    occupational_therapy: true,
    speech_therapy: true,
    prosthetics_orthotics: true,
    vocational_rehabilitation: true,
    psychological_services: true
  },
  
  examples: {
    ROMANIA: [
      'Central Military Emergency University Hospital "Dr. Carol Davila" - Bucharest',
      'Military Hospital "Dr. Alexandru Gafencu" - Constanta',
      'Military Hospital "Dr. Victor Popescu" - Timisoara'
    ],
    NATO: [
      'Landstuhl Regional Medical Center - Germany',
      'Walter Reed National Military Medical Center - USA',
      'Queen Elizabeth Hospital Birmingham - UK'
    ]
  },
  
  educational: {
    key_point: 'Final destination - definitive care, reconstruction, rehabilitation',
    teaching_note: 'On national territory, not in theater',
    role: 'Return to duty or transition to civilian care'
  }
};

// ============================================================================
// MTF CAPABILITIES BY ECHELON
// ============================================================================

export const MTF_CAPABILITIES_BATTALION = {
  echelon: 'BATTALION',
  icon: ICONS.GREEN,
  total_personnel: 25,
  
  role1: {
    count: 1,
    beds: 6,
    beds_type: 'Stretcher positions',
    surgical: false,
    surgical_teams: 0,
    hold_time_hours: 4,
    personnel: 25,
    icon: ICONS.MEDICAL,
    
    personnel_breakdown: {
      medical_officers: 2,
      physician_assistants: 1,
      nurses: 4,
      medics: 12,
      support_staff: 6,
      total: 25
    },
    
    capabilities: {
      emergency_care: true,
      basic_resuscitation: true,
      hemorrhage_control: true,
      airway_management: true,
      tourniquet_application: true,
      iv_fluids: true,
      pain_control: true,
      wound_dressing: true,
      splinting: true,
      triage: true,
      patient_tracking: true,
      evacuation_coordination: true
    },
    
    equipment: {
      stretchers: 12,
      oxygen_concentrators: 2,
      suction_units: 2,
      monitors: 4,
      defibrillator: 1,
      iv_pumps: 6,
      ventilator_transport: 1
    },
    
    consumables_dos: 3,
    typical_location: '5-8 km from FEBA'
  },
  
  role2: {
    count: 0,
    beds: 0,
    surgical_teams: 0,
    surgical: false,
    icon: null,
    note: 'Not present at battalion level'
  },
  
  role3: {
    count: 0,
    beds: 0,
    surgical_teams: 0,
    icu_beds: 0,
    icon: null,
    note: 'Not present at battalion level'
  },
  
  logistics: {
    power_kw: 10,
    water_liters_day: 200,
    resupply_frequency_days: 1,
    vehicles: 2
  }
};

export const MTF_CAPABILITIES_BRIGADE = {
  echelon: 'BRIGADE',
  icon: ICONS.YELLOW,
  total_personnel: 100,
  
  role1: {
    count: 1,
    beds: 6,
    beds_type: 'Stretcher positions',
    surgical: false,
    surgical_teams: 0,
    hold_time_hours: 4,
    personnel: 25,
    icon: ICONS.MEDICAL,
    
    personnel_breakdown: {
      medical_officers: 2,
      physician_assistants: 1,
      nurses: 4,
      medics: 12,
      support_staff: 6,
      total: 25
    },
    
    capabilities: {
      emergency_care: true,
      basic_resuscitation: true,
      triage: true,
      stabilization: true,
      evacuation_preparation: true
    },
    
    typical_location: '5-8 km from FEBA'
  },
  
  role2: {
    count: 1,
    beds: 30,
    beds_type: '20 stretcher + 10 recovery',
    surgical: true,
    surgical_teams: 2,
    icu_beds: 0,      // v2.2: Role 2 Basic has no ICU capability
    hold_time_hours: 72,
    personnel: 75,
    icon: ICONS.HOSPITAL,
    
    personnel_breakdown: {
      surgeons: 4,
      anesthesiologists: 2,
      nurses: 12,
      surgical_techs: 4,
      medics: 20,
      lab_techs: 3,
      xray_techs: 2,
      pharmacists: 1,
      blood_bank_techs: 2,
      admin_support: 8,
      logistics: 7,
      security: 10,
      total: 75
    },
    
    surgical_capability: {
      damage_control_surgery: true,
      laparotomy: true,
      thoracotomy: true,
      vascular_repair: 'Limited',
      orthopedic_damage_control: true,
      craniotomy: false,
      complex_procedures: false
    },
    
    capabilities: {
      advanced_resuscitation: true,
      damage_control_surgery: true,
      blood_transfusion: true,
      basic_xray: true,
      ultrasound: true,
      basic_laboratory: true,
      postoperative_monitoring: true
    },
    
    consumables_dos: 5,
    typical_location: '15-25 km from FEBA'
  },
  
  role3: {
    count: 0,
    beds: 0,
    surgical_teams: 0,
    icu_beds: 0,
    icon: null,
    note: 'Not present at brigade level'
  },
  
  logistics: {
    power_kw: 80,
    water_liters_day: 2000,
    resupply_frequency_days: 3,
    vehicles: 10
  }
};

export const MTF_CAPABILITIES_DIVISION = {
  echelon: 'DIVISION',
  icon: ICONS.ORANGE,
  total_personnel: 300,
  
  role1: {
    count: 3,
    beds: 18,
    beds_type: 'Stretcher positions',
    surgical: false,
    surgical_teams: 0,
    hold_time_hours: 4,
    personnel: 75,
    icon: ICONS.MEDICAL,
    
    personnel_breakdown: {
      medical_officers: 6,
      physician_assistants: 3,
      nurses: 12,
      medics: 36,
      support_staff: 18,
      total: 75
    },
    
    typical_location: '5-8 km from FEBA (distributed)'
  },
  
  role2: {
    count: 3,
    beds: 90,
    beds_type: '60 stretcher + 30 recovery',
    surgical: true,
    surgical_teams: 6,
    icu_beds: 0,      // v2.2: Role 2 Basic has no ICU (2 surgical teams each = R2B not R2E)
    hold_time_hours: 72,
    personnel: 225,
    icon: ICONS.HOSPITAL,
    
    personnel_breakdown: {
      surgeons: 12,
      anesthesiologists: 6,
      nurses: 36,
      surgical_techs: 12,
      medics: 60,
      lab_techs: 9,
      xray_techs: 6,
      pharmacists: 3,
      blood_bank_techs: 6,
      admin_support: 24,
      logistics: 21,
      security: 30,
      total: 225
    },
    
    surgical_capability: {
      damage_control_surgery: true,
      extended_procedures: true,
      laparotomy: true,
      thoracotomy: true,
      vascular_repair: true,
      orthopedic_procedures: true,
      limited_neurosurgery: true,
      craniotomy: 'Emergency only',
      complex_reconstructive: false
    },
    
    consumables_dos: 7,
    typical_location: '25-50 km from FEBA (distributed)'
  },
  
  role3: {
    count: 0,
    beds: 0,
    surgical_teams: 0,
    icu_beds: 0,
    icon: null,
    note: 'Not present at division level'
  },
  
  logistics: {
    power_kw: 250,
    water_liters_day: 8000,
    resupply_frequency_days: 3,
    vehicles: 30
  }
};

export const MTF_CAPABILITIES_CORPS = {
  echelon: 'CORPS',
  icon: ICONS.RED,
  total_personnel: 850,
  
  role1: {
    count: 6,
    beds: 36,
    beds_type: 'Stretcher positions',
    surgical: false,
    surgical_teams: 0,
    hold_time_hours: 4,
    personnel: 150,
    icon: ICONS.MEDICAL,
    
    personnel_breakdown: {
      medical_officers: 12,
      physician_assistants: 6,
      nurses: 24,
      medics: 72,
      support_staff: 36,
      total: 150
    },
    
    typical_location: '5-8 km from FEBA (distributed)'
  },
  
  role2: {
    count: 6,
    beds: 180,
    beds_type: '120 stretcher + 60 recovery',
    surgical: true,
    surgical_teams: 12,
    icu_beds: 0,      // v2.2: Role 2 Basic has no ICU capability (ICU is at Role 3)
    hold_time_hours: 72,
    personnel: 450,
    icon: ICONS.HOSPITAL,
    
    personnel_breakdown: {
      surgeons: 24,
      anesthesiologists: 12,
      nurses: 72,
      surgical_techs: 24,
      medics: 120,
      lab_techs: 18,
      xray_techs: 12,
      pharmacists: 6,
      blood_bank_techs: 12,
      admin_support: 48,
      logistics: 42,
      security: 60,
      total: 450
    },
    
    typical_location: '20-50 km from FEBA (distributed)'
  },
  
  role3: {
    count: 1,
    beds: 150,
    beds_type: '100 ward + 50 ICU',
    surgical: true,
    surgical_teams: 8,
    icu_beds: 40,
    hold_time_hours: 168,
    personnel: 250,
    icon: ICONS.HOSPITAL,
    
    personnel_breakdown: {
      surgeons: 16,
      anesthesiologists: 8,
      intensivists: 4,
      medical_officers: 8,
      nurses: 60,
      surgical_techs: 16,
      respiratory_therapists: 4,
      medics: 40,
      lab_technicians: 12,
      radiology_techs: 8,
      pharmacists: 4,
      blood_bank_staff: 6,
      physical_therapists: 2,
      mental_health: 4,
      admin_staff: 20,
      logistics: 20,
      security: 20,
      maintenance: 2,
      total: 250
    },
    
    surgical_capability: {
      definitive_surgery: true,
      all_specialties: true,
      complex_procedures: true,
      reconstructive_surgery: 'Limited',
      icu_capability: true
    },
    
    consumables_dos: 14,
    typical_location: '100-150 km from FEBA'
  },
  
  logistics: {
    power_kw: 500,
    water_liters_day: 25000,
    resupply_frequency_days: 5,
    vehicles: 80
  }
};

// ============================================================================
// MTF CAPABILITIES BY ECHELON MAP
// ============================================================================

export const MTF_CAPABILITIES_BY_ECHELON = {
  BATTALION: MTF_CAPABILITIES_BATTALION,
  BRIGADE: MTF_CAPABILITIES_BRIGADE,
  DIVISION: MTF_CAPABILITIES_DIVISION,
  CORPS: MTF_CAPABILITIES_CORPS
};

// ============================================================================
// MASCAL SURGE CAPACITY
// ============================================================================

export const MASCAL_SURGE_CAPACITY = {
  description: 'Mass Casualty surge capacity by MTF role',
  icon: ICONS.WARNING,
  
  ROLE_1: {
    normal_capacity: 6,
    surge_capacity: 12,
    surge_multiplier: 2.0,
    hold_time_surge_hours: 2,
    notes: 'Can double capacity by using floor space, vehicles'
  },
  
  ROLE_2: {
    normal_capacity: 30,
    surge_capacity: 50,
    surge_multiplier: 1.67,
    hold_time_surge_hours: 24,
    surgical_surge_multiplier: 1.5,
    notes: 'Limited by surgical team capacity'
  },
  
  ROLE_3: {
    normal_capacity: 150,
    surge_capacity: 250,
    surge_multiplier: 1.67,
    hold_time_surge_hours: 48,
    icu_surge_multiplier: 1.5,
    notes: 'Can expand wards, convert non-ICU beds'
  }
};

// ============================================================================
// BED OCCUPANCY MODEL
// ============================================================================

export const BED_OCCUPANCY_MODEL = {
  optimal_occupancy: 0.70,
  warning_occupancy: 0.85,
  critical_occupancy: 0.95,

  // DOW effect of a full ward is a RAMP, not a step (Dr. Tatar, 2026-10-10): none up to the optimal occupancy, then rising
  // smoothly to the full effect at 100% occupancy. The full effect is the multiplier of the critical capability
  // (getDOWMultiplierFromCapability(0.70), x1.5). Start and end are a PLACEHOLDER (Dr. Tatar's first estimate).
  dow_ramp: { start: 0.70, full: 1.00, full_effect_capability: 0.70 },
  
  status_thresholds: {
    GREEN: { max: 0.70, icon: ICONS.GREEN, label: 'Normal' },
    YELLOW: { min: 0.70, max: 0.85, icon: ICONS.YELLOW, label: 'Elevated' },
    ORANGE: { min: 0.85, max: 0.95, icon: ICONS.ORANGE, label: 'High' },
    RED: { min: 0.95, icon: ICONS.RED, label: 'Critical' }
  },
  
  turnover_rate: {
    role_1: 6,
    role_2: 2,
    role_3: 0.5
  }
};

// ============================================================================
// UKRAINE MTF LESSONS (2022-2024)
// ============================================================================

export const UKRAINE_MTF_LESSONS = {
  applicable: true,
  source: 'Ukraine 2022-2024 combat medical operations',
  icon: ICONS.WARNING,
  
  role_1_lessons: [
    'Stabilization points pushed forward (3-5 km from FEBA)',
    'Vehicle-based Role 1 provides better survivability',
    'Drone threat requires dispersed operations',
    'Night evacuation became primary due to drone surveillance'
  ],
  
  role_2_lessons: [
    'Civilian facilities converted to Role 2 rapidly',
    'Surgical capacity consistently 2x planned requirement',
    'Blood product shortage common - walking blood bank critical',
    'Damage control surgery saved lives but requires follow-on care'
  ],
  
  role_3_lessons: [
    'Civilian hospitals served as Role 3',
    'ICU beds were limiting factor',
    'Strategic evacuation to Poland/Germany essential',
    'Mental health needs severely underestimated'
  ],
  
  capacity_adjustments: {
    role_1_multiplier: 1.2,
    role_2_multiplier: 2.0,
    role_3_multiplier: 1.5,
    surgical_team_multiplier: 1.5,
    blood_supply_multiplier: 2.0
  },
  
  key_takeaway: 'Plan for 2x the surgical capacity you think you need'
};

// ============================================================================
// UI HELPER FUNCTIONS
// ============================================================================

/**
 * Get MTF role options for UI dropdown
 * @returns {Array} Role options
 */
export const getMTFRoleOptions = () => {
  return [
    {
      value: '1',
      label: 'Role 1 - Battalion Aid Station',
      icon: ROLE_1_DEFINITION.icon,
      color: ROLE_1_DEFINITION.color,
      beds: 6,
      surgical: false,
      abbreviation: 'BAS'
    },
    {
      value: '2B',
      label: 'Role 2 Basic - Forward Resuscitation',
      icon: ROLE_2_BASIC_DEFINITION.icon,
      color: ROLE_2_BASIC_DEFINITION.color,
      beds: 30,
      surgical: true,
      surgicalTeams: 2,
      abbreviation: 'R2B'
    },
    {
      value: '2E',
      label: 'Role 2 Enhanced - Forward Surgical',
      icon: ROLE_2_ENHANCED_DEFINITION.icon,
      color: ROLE_2_ENHANCED_DEFINITION.color,
      beds: 40,
      surgical: true,
      surgicalTeams: 4,
      abbreviation: 'R2E'
    },
    {
      value: '3',
      label: 'Role 3 - Combat Support Hospital',
      icon: ROLE_3_DEFINITION.icon,
      color: ROLE_3_DEFINITION.color,
      beds: 150,
      surgical: true,
      surgicalTeams: 8,
      hasICU: true,
      abbreviation: 'CSH'
    },
    {
      value: '4',
      label: 'Role 4 - Strategic Hospital',
      icon: ROLE_4_DEFINITION.icon,
      color: ROLE_4_DEFINITION.color,
      beds: 500,
      surgical: true,
      rehabilitation: true,
      abbreviation: 'R4'
    }
  ];
};

/**
 * Get echelon options for UI dropdown
 * @returns {Array} Echelon options
 */
export const getEchelonOptions = () => {
  return [
    {
      value: 'BATTALION',
      label: 'Battalion',
      icon: MTF_CAPABILITIES_BATTALION.icon,
      totalBeds: 6,
      surgicalTeams: 0,
      roles: ['Role 1']
    },
    {
      value: 'BRIGADE',
      label: 'Brigade',
      icon: MTF_CAPABILITIES_BRIGADE.icon,
      totalBeds: 36,
      surgicalTeams: 2,
      roles: ['Role 1', 'Role 2 Basic']
    },
    {
      value: 'DIVISION',
      label: 'Division',
      icon: MTF_CAPABILITIES_DIVISION.icon,
      totalBeds: 108,
      surgicalTeams: 6,
      roles: ['Role 1 (×3)', 'Role 2 Enhanced (×3)']
    },
    {
      value: 'CORPS',
      label: 'Corps',
      icon: MTF_CAPABILITIES_CORPS.icon,
      totalBeds: 366,
      surgicalTeams: 20,
      roles: ['Role 1 (×6)', 'Role 2 (×6)', 'Role 3']
    }
  ];
};

/**
 * Get bed occupancy status options
 * @returns {Array} Status options
 */
export const getBedOccupancyStatusOptions = () => {
  return Object.entries(BED_OCCUPANCY_MODEL.status_thresholds).map(([key, status]) => ({
    value: key,
    label: status.label,
    icon: status.icon,
    maxOccupancy: status.max || 1.0
  }));
};

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Get MTF capabilities for an echelon
 * @param {string} echelon - BATTALION, BRIGADE, DIVISION, CORPS
 * @returns {Object} MTF capabilities
 */
export const getMTFCapabilities = (echelon) => {
  return MTF_CAPABILITIES_BY_ECHELON[echelon?.toUpperCase()] || null;
};

/**
 * Get total beds for an echelon
 * @param {string} echelon - Echelon name
 * @returns {number} Total beds
 */
export const getTotalBeds = (echelon) => {
  const capabilities = getMTFCapabilities(echelon);
  if (!capabilities) return 0;
  
  return (capabilities.role1?.beds || 0) + 
         (capabilities.role2?.beds || 0) + 
         (capabilities.role3?.beds || 0);
};

/**
 * Get total surgical teams for an echelon
 * @param {string} echelon - Echelon name
 * @returns {number} Total surgical teams
 */
export const getTotalSurgicalTeams = (echelon) => {
  const capabilities = getMTFCapabilities(echelon);
  if (!capabilities) return 0;
  
  return (capabilities.role2?.surgical_teams || 0) + 
         (capabilities.role3?.surgical_teams || 0);
};

/**
 * Get total medical personnel for an echelon
 * @param {string} echelon - Echelon name
 * @returns {number} Total personnel
 */
export const getTotalPersonnel = (echelon) => {
  const capabilities = getMTFCapabilities(echelon);
  return capabilities?.total_personnel || 0;
};

/**
 * Check if echelon has surgical capability
 * @param {string} echelon - Echelon name
 * @returns {boolean} Has surgery
 */
export const hasSurgicalCapability = (echelon) => {
  return echelon?.toUpperCase() !== 'BATTALION';
};

/**
 * Check if echelon has ICU capability
 * @param {string} echelon - Echelon name
 * @returns {boolean} Has ICU
 */
export const hasICUCapability = (echelon) => {
  return echelon?.toUpperCase() === 'CORPS';
};

/**
 * Get MTF structure for a specific role and echelon
 * @param {string} echelon - Echelon name
 * @param {string} role - Role number
 * @returns {Object|null} Role configuration
 */
export const getMTFRole = (echelon, role) => {
  const capabilities = getMTFCapabilities(echelon);
  if (!capabilities) return null;
  
  const roleKey = `role${role}`;
  return capabilities[roleKey] || null;
};

/**
 * Calculate bed capacity by type
 * @param {string} echelon - Echelon name
 * @returns {Object} Bed capacity breakdown
 */
export const getBedCapacityByType = (echelon) => {
  const capabilities = getMTFCapabilities(echelon);
  if (!capabilities) return null;
  
  return {
    role1_stretchers: capabilities.role1?.beds || 0,
    role2_surgical_beds: capabilities.role2?.beds || 0,
    role3_ward_beds: capabilities.role3?.beds ? Math.floor(capabilities.role3.beds * 0.67) : 0,
    role3_icu_beds: capabilities.role3?.icu_beds || 0,
    total_beds: getTotalBeds(echelon),
    icon: capabilities.icon
  };
};

/**
 * Get surgical capabilities summary
 * @param {string} echelon - Echelon name
 * @returns {Object} Surgical capabilities
 */
export const getSurgicalCapabilities = (echelon) => {
  const capabilities = getMTFCapabilities(echelon);
  if (!capabilities) return null;
  
  return {
    has_surgery: hasSurgicalCapability(echelon),
    total_surgical_teams: getTotalSurgicalTeams(echelon),
    role2_teams: capabilities.role2?.surgical_teams || 0,
    role3_teams: capabilities.role3?.surgical_teams || 0,
    damage_control_surgery: echelon?.toUpperCase() !== 'BATTALION',
    definitive_surgery: echelon?.toUpperCase() === 'CORPS',
    icon: hasSurgicalCapability(echelon) ? ICONS.SURGERY : null
  };
};

/**
 * Calculate patient throughput capacity
 * @param {string} echelon - Echelon name
 * @param {number} hoursPerDay - Operating hours
 * @returns {Object} Throughput capacity
 */
export const calculatePatientThroughput = (echelon, hoursPerDay = 16) => {
  const capabilities = getMTFCapabilities(echelon);
  if (!capabilities) return null;
  
  const role1Throughput = (capabilities.role1?.count || 0) * 
                          (ROLE_1_DEFINITION.patient_throughput.normal_per_hour * hoursPerDay);
  
  let role2Throughput = 0;
  if (capabilities.role2?.count > 0) {
    role2Throughput = capabilities.role2.count * 
                      (ROLE_2_BASIC_DEFINITION.patient_throughput.normal_per_hour * hoursPerDay);
  }
  
  let role3Throughput = 0;
  if (capabilities.role3?.count > 0) {
    role3Throughput = capabilities.role3.count *
                      (ROLE_3_DEFINITION.patient_throughput.normal_per_hour * hoursPerDay);
  }
  
  return {
    role1: role1Throughput,
    role2: role2Throughput,
    role3: role3Throughput,
    total: role1Throughput + role2Throughput + role3Throughput,
    icon: ICONS.HOSPITAL
  };
};

/**
 * Calculate surgical capacity per day
 * @param {string} echelon - Echelon name
 * @returns {Object} Surgical capacity
 */
export const calculateSurgicalCapacityPerDay = (echelon) => {
  const capabilities = getMTFCapabilities(echelon);
  if (!capabilities) return null;
  
  const role2Procedures = (capabilities.role2?.surgical_teams || 0) * 4;
  const role3Procedures = (capabilities.role3?.surgical_teams || 0) * 4;
  
  return {
    role2_procedures: role2Procedures,
    role3_procedures: role3Procedures,
    total_procedures: role2Procedures + role3Procedures,
    dcs_capable: role2Procedures,
    definitive_capable: role3Procedures,
    icon: ICONS.SURGERY
  };
};

/**
 * Get surge capacity for echelon
 * @param {string} echelon - Echelon name
 * @returns {Object} Surge capacity
 */
export const getSurgeCapacity = (echelon) => {
  const normal = getTotalBeds(echelon);
  const surgeCap = MASCAL_SURGE_CAPACITY;
  
  let surgeBeds = 0;
  if (echelon?.toUpperCase() === 'BATTALION') {
    surgeBeds = surgeCap.ROLE_1.surge_capacity;
  } else if (echelon?.toUpperCase() === 'BRIGADE') {
    surgeBeds = surgeCap.ROLE_1.surge_capacity + surgeCap.ROLE_2.surge_capacity;
  } else if (echelon?.toUpperCase() === 'DIVISION') {
    surgeBeds = (surgeCap.ROLE_1.surge_capacity * 3) + (surgeCap.ROLE_2.surge_capacity * 3);
  } else if (echelon?.toUpperCase() === 'CORPS') {
    surgeBeds = (surgeCap.ROLE_1.surge_capacity * 6) + (surgeCap.ROLE_2.surge_capacity * 6) + surgeCap.ROLE_3.surge_capacity;
  }
  
  return {
    normal_capacity: normal,
    surge_capacity: surgeBeds,
    surge_multiplier: normal > 0 ? surgeBeds / normal : 0,
    surge_duration_hours: 24,
    icon: surgeBeds > normal ? ICONS.WARNING : ICONS.GREEN
  };
};

/**
 * Calculate bed occupancy
 * @param {number} currentPatients - Current patient count
 * @param {string} echelon - Echelon name
 * @returns {Object} Occupancy status
 */
export const calculateBedOccupancy = (currentPatients, echelon) => {
  const totalBeds = getTotalBeds(echelon);
  if (totalBeds === 0) return null;
  
  const occupancy = currentPatients / totalBeds;
  
  let status = 'GREEN';
  let icon = ICONS.GREEN;
  if (occupancy >= BED_OCCUPANCY_MODEL.critical_occupancy) {
    status = 'RED';
    icon = ICONS.RED;
  } else if (occupancy >= BED_OCCUPANCY_MODEL.warning_occupancy) {
    status = 'ORANGE';
    icon = ICONS.ORANGE;
  } else if (occupancy >= BED_OCCUPANCY_MODEL.optimal_occupancy) {
    status = 'YELLOW';
    icon = ICONS.YELLOW;
  }
  
  return {
    total_beds: totalBeds,
    occupied: currentPatients,
    available: totalBeds - currentPatients,
    occupancy_rate: Math.round(occupancy * 100) / 100,
    occupancy_percent: Math.round(occupancy * 100),
    status,
    icon,
    can_accept: Math.max(0, totalBeds - currentPatients),
    surge_available: getSurgeCapacity(echelon).surge_capacity - currentPatients
  };
};

/**
 * Get logistics requirements for echelon
 * @param {string} echelon - Echelon name
 * @returns {Object} Logistics requirements
 */
export const getLogisticsRequirements = (echelon) => {
  const capabilities = getMTFCapabilities(echelon);
  return capabilities?.logistics || {
    power_kw: 0,
    water_liters_day: 0,
    resupply_frequency_days: 1,
    vehicles: 0
  };
};

// ============================================================================
// VALIDATION FUNCTIONS
// ============================================================================

/**
 * Validate MTF configuration
 * @param {string} echelon - Echelon name
 * @param {Object} configuration - Configuration to validate
 * @returns {Object} Validation result
 */
export const validateMTFConfiguration = (echelon, configuration) => {
  const capabilities = getMTFCapabilities(echelon);
  const errors = [];
  const warnings = [];
  
  if (!capabilities) {
    errors.push({
      field: 'echelon',
      message: `Invalid echelon: ${echelon}`,
      icon: ICONS.RED
    });
    return { valid: false, errors, warnings, icon: ICONS.RED };
  }
  
  if (configuration.role1) {
    if (configuration.role1.count > capabilities.role1.count) {
      errors.push({
        field: 'role1',
        message: `Role 1 count exceeds standard for ${echelon}`,
        icon: ICONS.RED
      });
    }
  }
  
  if (configuration.role2) {
    if (echelon?.toUpperCase() === 'BATTALION' && configuration.role2.count > 0) {
      errors.push({
        field: 'role2',
        message: 'Role 2 facilities not available at Battalion level',
        icon: ICONS.RED
      });
    }
    if (configuration.role2.count > (capabilities.role2?.count || 0)) {
      warnings.push({
        field: 'role2',
        message: `Role 2 count exceeds standard for ${echelon}`,
        icon: ICONS.YELLOW
      });
    }
  }
  
  if (configuration.role3) {
    if (echelon?.toUpperCase() !== 'CORPS' && configuration.role3.count > 0) {
      errors.push({
        field: 'role3',
        message: 'Role 3 facilities only available at Corps level',
        icon: ICONS.RED
      });
    }
  }
  
  // Check Ukraine lessons
  if (configuration.apply_ukraine_lessons) {
    warnings.push({
      field: 'capacity',
      message: 'Consider 2x surgical capacity based on Ukraine data',
      icon: ICONS.WARNING
    });
  }
  
  return {
    valid: errors.length === 0,
    errors,
    warnings,
    icon: errors.length > 0 ? ICONS.RED : (warnings.length > 0 ? ICONS.YELLOW : ICONS.GREEN)
  };
};

/**
 * Check if echelon is valid
 * @param {string} echelon - Echelon name
 * @returns {boolean} True if valid
 */
export const isValidEchelon = (echelon) => {
  return echelon?.toUpperCase() in MTF_CAPABILITIES_BY_ECHELON;
};

/**
 * Get all valid echelons
 * @returns {Array} Valid echelon names
 */
export const getEchelons = () => ['BATTALION', 'BRIGADE', 'DIVISION', 'CORPS'];

/**
 * Get all valid roles
 * @returns {Array} Valid role numbers
 */
export const getRoles = () => ['1', '2', '3', '4'];

/**
 * Format MTF name
 * @param {string} echelon - Echelon name
 * @param {string} role - Role number
 * @returns {string} Formatted name
 */
export const formatMTFName = (echelon, role) => {
  const names = {
    '1': 'Battalion Aid Station (Role 1)',
    '2': echelon?.toUpperCase() === 'BRIGADE' ? 'Forward Resuscitation (Role 2 Basic)' : 'Forward Surgical Team (Role 2 Enhanced)',
    '3': 'Combat Support Hospital (Role 3)',
    '4': 'Strategic Hospital (Role 4)'
  };
  return names[role] || `Role ${role}`;
};

/**
 * Get personnel breakdown for echelon
 * @param {string} echelon - Echelon name
 * @returns {Object} Personnel breakdown
 */
export const getPersonnelBreakdown = (echelon) => {
  const capabilities = getMTFCapabilities(echelon);
  if (!capabilities) return null;
  
  return {
    role1_personnel: capabilities.role1?.personnel || 0,
    role2_personnel: capabilities.role2?.personnel || 0,
    role3_personnel: capabilities.role3?.personnel || 0,
    total_personnel: capabilities.total_personnel || 0,
    icon: ICONS.MEDICAL
  };
};

// ============================================================================
// DEFAULT EXPORT
// ============================================================================

// Named default export (fixes lint warning)
const mtfCapabilitiesConstants = {
  // Role Definitions
  ROLE_1_DEFINITION,
  ROLE_2_BASIC_DEFINITION,
  ROLE_2_ENHANCED_DEFINITION,
  ROLE_3_DEFINITION,
  ROLE_4_DEFINITION,
  
  // Capabilities by Echelon
  MTF_CAPABILITIES_BATTALION,
  MTF_CAPABILITIES_BRIGADE,
  MTF_CAPABILITIES_DIVISION,
  MTF_CAPABILITIES_CORPS,
  MTF_CAPABILITIES_BY_ECHELON,
  
  // Surge and Occupancy
  MASCAL_SURGE_CAPACITY,
  BED_OCCUPANCY_MODEL,
  
  // Lessons Learned
  UKRAINE_MTF_LESSONS,
  
  // UI Options
  getMTFRoleOptions,
  getEchelonOptions,
  getBedOccupancyStatusOptions,
  
  // Helper Functions
  getMTFCapabilities,
  getTotalBeds,
  getTotalSurgicalTeams,
  getTotalPersonnel,
  hasSurgicalCapability,
  hasICUCapability,
  getMTFRole,
  getBedCapacityByType,
  getSurgicalCapabilities,
  calculatePatientThroughput,
  calculateSurgicalCapacityPerDay,
  getSurgeCapacity,
  calculateBedOccupancy,
  getLogisticsRequirements,
  
  // Validation
  validateMTFConfiguration,
  isValidEchelon,
  getEchelons,
  getRoles,
  formatMTFName,
  getPersonnelBreakdown
};

export default mtfCapabilitiesConstants;
